import React, { useState, useEffect, useCallback } from 'react';
import './App.css'; // We'll create this file for styling
import { MathJaxContext, MathJax } from 'better-react-mathjax';

const targetHash = 500.07
const _15min = 15 * 60 * 1000
const App: React.FC = () => {
  const [input, setInput] = useState<string>('');
  const [result, setResult] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState('tab1');
  const [minerNonce, setMinerNonce] = useState<string>('1');
  const [minerBestNonce, setMinerBestNonce] = useState<string>('1');
  const [minerInput, setMinerInput] = useState<string>('100000000000');
  const [minerCalculatedInput, setMinerCalculatedInput] = useState<string>('100000000001');
  const [minerBestHash, setMinerBestHash] = useState<string>('1000');
  const [minerIsMining, setMinerIsMining] = useState<boolean>(false)
  const [minerStep, setMinerStep] = useState<number>(1)
  const [minerStartTime, setMinerStartTime] = useState<number|null>(null)
  const [minerShowFoundNonceNotification, setMinerShowFoundNonceNotification] = useState<boolean>(false)

  const updateResult = useCallback((_input: string) => {
    const inputNumber = parseFloat(_input);
    if (!isNaN(inputNumber)) {
      setResult(Number(calculateSinHash500(inputNumber).toPrecision(8)));
    }
   }, []);

  const miningIteration = useCallback(() => {
    var step = minerStep
    if (minerStep === 1) {
      step = 1.2 + Math.random() * 0.2
      setMinerStep(step)
    }
    if (minerStartTime === null) {
      setMinerStartTime(Date.now())
    }
    var nonceNum = parseFloat(minerNonce)
    nonceNum += step
    setMinerNonce(String(nonceNum.toPrecision(8)))
    const inputNum = parseInt(minerInput)
    if (100*nonceNum >= inputNum)
    {
      nonceNum = 0 + Math.random()
      setMinerNonce(nonceNum.toPrecision(8))
    }
  
    var bestHash = "1000"
    if (minerBestHash === '') {
      setMinerBestHash("1000")
    } else {
      bestHash = minerBestHash
    }
  
    const bestHashNum = parseFloat(bestHash)
    const calculatedInputNum = Number((1 + inputNum/nonceNum).toPrecision(8))
    const hash = calculateSinHash500(calculatedInputNum)
    if ((hash > 500) && (hash < bestHashNum)){
      setMinerBestHash(String(hash.toPrecision(8))) 
      setMinerBestNonce(String(nonceNum))
      if (hash < targetHash) {
        setMinerShowFoundNonceNotification(true)
      }
      
      const c = String(calculatedInputNum.toLocaleString('fullwide', {useGrouping:false}))
      setMinerCalculatedInput(c)
      setInput(c)
      updateResult(c)
    }
  }, [minerBestHash, minerInput, minerNonce, minerStartTime, minerStep, updateResult])

  useEffect(() => {
    const mineAndWait = async () => {
      await new Promise(f => setTimeout(f, 1));
      miningIteration();}

    if (minerIsMining){
      mineAndWait()
      const now = Date.now()
      if (typeof minerStartTime === "number")
      {
        if (now - minerStartTime > _15min) {
          setMinerIsMining(false)
          setMinerStartTime(null)
        }
      }
    }
  }, [minerIsMining, minerStartTime, miningIteration])

  const calculateSinHash500 = (x: number): number => {
    return 500 * Math.sin(500 * x) + 500;
  };

  const handleCalculate = () => {
    updateResult(input)
  };

  const toggleIsMining = () => {
    setMinerIsMining(!minerIsMining)
  };

  return (
    <MathJaxContext>
    <div className="App">
      <div className="container">
        <div className="tab-buttons">
          <button onClick={() => setActiveTab('tab1')} className={activeTab === 'tab1' ? 'active tab-button' : 'tab-button'}>Hash Calculator</button>
          <button onClick={() => setActiveTab('tab2')} className={activeTab === 'tab2' ? 'active tab-button' : 'tab-button'}>Miner</button>
        </div>
        {activeTab === 'tab1' && <div className="tab-content">
          <h2 className="title">SinHash-500 Calculator</h2>
          <div className="definition">
          <MathJax>{"\\(SinHash\\text{-}500(x) = 500\\sin(500x) + 500\\)"}</MathJax>

          </div>
          <div className="input-container">
            <input
              type="number"
              value={input}
              onChange={(e) => {
                setInput(e.target.value)
                updateResult(e.target.value)
              }}
              placeholder="Enter input (number)"
              className="input"
            />
            <button onClick={handleCalculate} className="button">Calculate</button>
          </div>
          {result === null && (
            <div className="definition">
              <MathJax>{"\\(SinHash\\text{-}500(x) = ?\\)"}</MathJax>
            </div>
          )}
          {result !== null && (
            <p>
              Result: {result}
            </p>
        )}
        </div>}
        {activeTab === 'tab2' && <div className="tab-content">
          {minerShowFoundNonceNotification === true && <div className="notification-box success">
            <span className="icon">🎊</span>
            <span className="message-content">Congratulations! You found a valid nonce! </span>
            </div>}
        <h2 className="title">SinHash-500 Miner</h2>
          <div className="definition">
            <MathJax>{"\\(500 < SinHash\\text{-}500\\left(1 + \\frac{input}{nonce}\\right) < target\\text{ }hash\\)"}</MathJax>      
          </div>
          <p className='card'>
            The goal is to find nonce so the result of the hash will be between 500 and the current target hash 
          </p>
          <div className='card-container'>
            <p>Target hash: {targetHash}</p>
            <p>Best nonce: {minerBestNonce}</p>
            <p>Calculated Input: {minerCalculatedInput}</p>
            <p>Best hash: {minerBestHash}</p>
          </div>
          
          <div className="from-container">
            <div className="form-group">
              <label>Input</label>
              <input
                type="number"
                value={minerInput}
                onChange={(e) => {
                  setMinerInput(e.target.value)
                setMinerBestHash("1000")
                setMinerBestNonce("1")
                setMinerCalculatedInput(String((parseFloat(minerInput)/parseFloat(minerNonce)).toLocaleString('fullwide', {useGrouping:false})))
                }}
                placeholder="Enter input (number)"
                className="input"
              />
            </div>
          
            <div className="form-group">
              <label>Start nonce</label>
              <input
                type="number"
                value={minerNonce}
                onChange={(e) => {
                  setMinerNonce(e.target.value)
                }}
                placeholder="Start nonce"
                className="input"
              />
          </div>         
          </div>
          <button onClick={toggleIsMining} className="button">{minerIsMining && "Stop"}{!minerIsMining && "Start"}</button>
        </div>}

      </div>
    </div>
    </MathJaxContext>
  );
};

export default App;